import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";

// --- Mock Data ---
const faqs = [
    {
        category: "Services",
        q: "How is Fortune Signals different from other trading academies?",
        a: "Unlike conventional trading schools, Fortune Signals teaches predictive, time-driven methods. We combine Time Trading Technique, Astro Techniques, and Gann Time Cycles, unlike other Learning that rely on RSI, EMA, MACD, or price action.\n\nBased in Dubai and serving international traders in UAE, GCC, UK, Europe, and Asia, we offer indicator-free trading mastery and elite mentorship.",
    },
    {
        category: "Trading",
        q: "What is Time Trading Technique?",
        a: "Time Trading Technique is our proprietary time-based trading system designed to forecast market movements before they appear in price.\n\nUnlike traditional charting methods or indicator-based trading like RSI, EMA, or Price Action, our methodology focuses on Time Cycle Trading, predictive market forecasting, and Astro Market Analysis. It works across Forex, US Stocks, Commodities, and Crypto markets.",
    },
    {
        category: "Trading",
        q: "What are Astro Techniques in trading?",
        a: "Astro Techniques use planetary and celestial cycles to understand financial market behavior. Integrated into our Time Cycle Trading programs, these techniques provide predictive insights for Forex, Crypto, Gold, Crude Oil, US Stocks, and global indices.",
    },
    {
        category: "Learning",
        q: "Do I need charting or indicators to learn from Fortune Signals?",
        a: "No. Our Learning are 100% indicator-free. You don’t need RSI, EMA, MACD, or Price Action knowledge. Instead, we teach predictive trading techniques using Time Cycle Trading, Astro Market Analysis, and Gann Time Cycles.",
    },
    {
        category: "Learning",
        q: "Can beginners learn Time Cycle & Astro Techniques?",
        a: "Absolutely. Our programs are designed for both beginners and professional traders. Through structured, step-by-step Time-Based Trading Learning, learners gain a deep understanding of market trends across Forex, Stocks, Commodities, and Crypto.",
    },
    {
        category: "Learning",
        q: "Do you offer personal mentorship?",
        a: "Yes. We provide one-on-one online mentorship programs that include strategy development, market analysis guidance, and personalized support.",
    },
    {
        category: "Learning",
        q: "How do I start learning?",
        a: "Simply click “Join Time Technique Trading Mastery™” or “Book a Discovery Call” on our homepage. You will gain access to:\n\n• Elite Time-Based Trading programs\n• Global mentorship in Forex, Stocks, Crypto & Commodities\n• Predictive market timing insights\n• Access to the international Fortune Signals trading community",
    },
    {
        category: "Support",
        q: "What support do I get as an international trader?",
        a: "Traders globally receive:\n\n• Mentorship by Mr. R S Parthasarathy and Mr. Vinayakk V Joshi\n• Access to our global trading community\n• Weekly Time Cycle Forecasts for Forex, US Stocks, Commodities, and Crypto\n• Practical guidance in Time-Based Trading techniques",
    },
];

const filters = ["All", "Services", "Trading", "Learning", "Support"];

const FAQ = () => {
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("All");
    const [openIndex, setOpenIndex] = useState(null);

    const filteredFAQs = faqs.filter(
        (f) =>
            (activeFilter === "All" || f.category === activeFilter) &&
            f.q.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <section id="faq" className="relative w-full py-16 sm:py-20 lg:py-24 px-4 sm:px-8 bg-slate-50 dark:bg-slate-950 min-h-screen bg-gradient-to-b from-bg-light-1 to-bg-light-2 dark:from-bg-dark-1 dark:to-bg-dark-2 overflow-hidden font-poppins">

            <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center">

                {/* HEADER */}
                <div className="text-center mb-10 sm:mb-12">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-primary uppercase">
                                Help Center
                            </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        /* Typography Logic:
                           text-3xl (XS/Mobile) -> text-4xl (Tablet) -> text-5xl (Desktop) -> text-6xl (XL)
                        */
                        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-dark dark:text-light leading-[1.2] tracking-tight"
                    >
                        Frequently Asked Questions
                    </motion.h2>
                </div>

                {/* SEARCH BAR */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative w-full max-w-xl mb-8 sm:mb-10"
                >
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                        <Search size={20} />
                    </div>
                    <input
                        type="text"
                        placeholder="Search for answers..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="
                          w-full pl-11 sm:pl-12 pr-5 py-3 sm:py-4 rounded-2xl
                          bg-white dark:bg-slate-900
                          text-slate-900 dark:text-slate-100 text-sm sm:text-base
                          border border-slate-200 dark:border-slate-800
                          shadow-lg shadow-slate-200/50 dark:shadow-none
                          focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                          outline-none transition-all duration-300
                          placeholder:text-slate-400
                        "
                    />
                </motion.div>

                {/* FILTER TABS */}
                <div className="flex flex-wrap justify-center gap-2 mb-10 sm:mb-12">
                    {filters.map((f) => (
                        <button
                            key={f}
                            onClick={() => setActiveFilter(f)}
                            className="relative px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                            {activeFilter === f && (
                                <motion.div
                                    layoutId="activeFilter"
                                    className="absolute inset-0 bg-blue-600 dark:bg-blue-600 rounded-full"
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                />
                            )}
                            <span className={`relative z-10 ${activeFilter === f ? "text-white" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}>
                                {f}
                            </span>
                        </button>
                    ))}
                </div>

                {/* FAQ LIST */}
                <motion.div layout className="w-full max-w-3xl flex flex-col gap-3 sm:gap-4">
                    <AnimatePresence mode="popLayout">
                        {filteredFAQs.map((item, i) => (
                            <FAQItem
                                key={item.q}
                                q={item.q}
                                a={item.a}
                                isOpen={openIndex === i}
                                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                            />
                        ))}
                    </AnimatePresence>

                    {filteredFAQs.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-10"
                        >
                            <div className="inline-block p-4 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 mb-3">
                                <Search size={24} />
                            </div>
                            <p className="text-slate-500 dark:text-slate-400">No questions found matching "{search}"</p>
                            <button
                                onClick={() => {setSearch(""); setActiveFilter("All")}}
                                className="mt-2 text-blue-600 dark:text-blue-400 font-medium hover:underline"
                            >
                                Clear search
                            </button>
                        </motion.div>
                    )}
                </motion.div>

            </div>
        </section>
    );
};

export default FAQ;

/* ------------------------------------------------------------ */
/*                       SINGLE FAQ ITEM                        */
/* ------------------------------------------------------------ */

function FAQItem({ q, a, isOpen, onClick }) {
    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={onClick}
            className={`
                group relative overflow-hidden rounded-xl border cursor-pointer transition-all duration-300
                ${isOpen
                ? "bg-white dark:bg-slate-900 border-blue-500/30 shadow-lg shadow-blue-500/10 ring-1 ring-blue-500/20"
                : "bg-white/50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 hover:border-blue-400/50 hover:bg-white dark:hover:bg-slate-900"
            }
            `}
        >
            {/* Question Header */}
            {/* Padding Logic: p-4 on Mobile (XS), px-6 py-5 on Tablet/Desktop (SM+) */}
            <div className="p-4 sm:px-6 sm:py-5 flex justify-between items-start sm:items-center gap-4">
                <h3 className={`text-base sm:text-lg font-semibold transition-colors duration-300 leading-snug ${isOpen ? "text-blue-600 dark:text-blue-400" : "text-slate-800 dark:text-slate-200"}`}>
                    {q}
                </h3>

                <div className={`
                    flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 mt-0.5 sm:mt-0
                    ${isOpen ? "bg-blue-600 text-white rotate-180" : "bg-slate-100 dark:bg-slate-800 text-slate-500 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600"}
                `}>
                    <ChevronDown size={16} className="sm:w-[18px] sm:h-[18px]" />
                </div>
            </div>

            {/* Answer Body */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="px-4 pb-4 sm:px-6 sm:pb-5 pt-0">
                            {/* whitespace-pre-line ensures \n and bullet points render correctly */}
                            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">
                                {a}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}